import { DownloadOutlined } from '@ant-design/icons';
import { DailyCount } from '@models/statistics';
import { Button, Card, Segmented, Table } from 'antd';
import { ChartData } from 'chart.js';
import moment from 'moment';
import React, { useMemo, useState } from 'react';
import { Line } from 'react-chartjs-2';
import ClipLoader from 'react-spinners/ClipLoader';
import * as XLSX from 'xlsx';

type ViewMode = 'chart' | 'table';

const formatDailyCountDate = (item: DailyCount) => {
  const month = String(item._id.month).padStart(2, '0');
  const day = String(item._id.day).padStart(2, '0');
  return `${item._id.year}-${month}-${day}`;
};

interface DailyCountChartCardProps {
  title: string;
  countColumnTitle: string;
  fileNamePrefix: string;
  isLoading: boolean;
  chartData: ChartData<'line'>;
  items: DailyCount[] | null;
  startDate: string | null;
  endDate: string | null;
}

export const DailyCountChartCard: React.FC<DailyCountChartCardProps> = ({
  title,
  countColumnTitle,
  fileNamePrefix,
  isLoading,
  chartData,
  items,
  startDate,
  endDate,
}) => {
  const [viewMode, setViewMode] = useState<ViewMode>('chart');

  const tableRows = useMemo(
    () =>
      [...(items || [])]
        .map((item) => ({
          key: formatDailyCountDate(item),
          date: formatDailyCountDate(item),
          count: item.count,
        }))
        .sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0)),
    [items],
  );

  const downloadExcel = () => {
    if (!tableRows.length) {
      return;
    }

    const total = tableRows.reduce((sum, row) => sum + (row.count || 0), 0);
    const sheetData = [['날짜', countColumnTitle], ...tableRows.map((row) => [row.date, row.count]), ['합계', total]];
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.aoa_to_sheet(sheetData);
    XLSX.utils.book_append_sheet(workbook, worksheet, title.slice(0, 31));

    const fileName = `${fileNamePrefix}-${moment(startDate).format('YYYYMMDD')}-${moment(endDate).format(
      'YYYYMMDD',
    )}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };

  return (
    <Card
      className="chart"
      bordered={false}
      title={title}
      extra={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Segmented
            size="small"
            value={viewMode}
            onChange={(value) => setViewMode(value as ViewMode)}
            options={[
              { label: '차트', value: 'chart' },
              { label: '표', value: 'table' },
            ]}
          />
          <Button
            size="small"
            icon={<DownloadOutlined />}
            onClick={downloadExcel}
            disabled={isLoading || tableRows.length === 0}>
            엑셀
          </Button>
        </div>
      }>
      {isLoading && <ClipLoader />}
      {!isLoading && viewMode === 'chart' && <Line data={chartData} />}
      {!isLoading && viewMode === 'table' && (
        <Table
          size="small"
          bordered
          pagination={false}
          scroll={{ y: 240 }}
          dataSource={tableRows}
          columns={[
            {
              title: '날짜',
              dataIndex: 'date',
              align: 'center',
            },
            {
              title: countColumnTitle,
              dataIndex: 'count',
              align: 'right',
              render: (count: number) => count.toLocaleString(),
            },
          ]}
        />
      )}
    </Card>
  );
};

export default DailyCountChartCard;
